import React, { Component } from 'react';
import { Flex, Button, TextField, Dropdown, Modal, ModalContent, ModalFooterButtons } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./random-item-wizard.module.scss";
import { showNotice } from '../../services/error-handling';
import mondaySdk from "monday-sdk-js";
import { getBoardColumns, getBoardGroups, createBoardColumn, createBoardView } from '../../services/api-service';
import { pickRandomItems } from '../../services/random-picker-service';
import { valueCreated } from '../../services/hooks';

const STRING_OPERATORS = [
  { label: "is", value: "eq" },
  { label: "is not", value: "neq" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "not_contains" },
  { label: "starts with", value: "starts_with" },
  { label: "ends with", value: "ends_with" }
];

const COMPARATOR_OPERATORS = [
  { label: "is", value: "eq" },
  { label: "is not", value: "neq" },
  { label: "greater than", value: "gt" },
  { label: "greater than or equals", value: "gte" },
  { label: "less than", value: "lt" },
  { label: "less than or equals", value: "lte" }
];

const COMPARATOR_COLUMN_TYPES = new Set(["numeric", "numbers", "date"]);

const ALLOWED_COLUMN_TYPES = new Set([
  "text",
  "long_text",
  "status",
  "dropdown",
  "tags",
  "tag",
  "item_id",
  "numeric",
  "numbers",
  "date"
]);

const ITEM_ID_COLUMN_TITLE = "Random Item Picker Item IDs";

const DEFAULT_ITEMS_TO_SELECT = 1;
const MAX_ITEMS_TO_SELECT = 1000;

const getOperatorOptionsForType = (columnType) =>
  columnType && COMPARATOR_COLUMN_TYPES.has(columnType)
    ? COMPARATOR_OPERATORS
    : STRING_OPERATORS;

class RandomItemWizard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      boardId: null,
      userId: null,
      columns: [],
      filters: [],
      groups: [],
      selectedGroupIds: [],
      itemsToSelect: DEFAULT_ITEMS_TO_SELECT,
      isInitializing: true,
      isDrawing: false,
      drawnItems: [],
      showResultsModal: false,
      viewNameInput: `Random Picker - ${new Date().toLocaleString()}`,
      createViewLoading: false,
      ensureColumnLoading: false,
      showColumnPrompt: false
    };
    this.monday = mondaySdk();
  }

  componentDidMount() {
    this.monday.listen("context", async (res) => {
      const context = res.data || {};
      const boardId = context.boardId ?? null;

      this.setState(
        {
          userId: context.user?.id ?? null,
          boardId,
          isInitializing: true
        },
        async () => {
          if (boardId) {
            await this.loadBoardData(boardId);
          } else {
            this.setState({ isInitializing: false });
            showNotice("error", "Open this view from a board to configure a random draw.");
          }
        }
      );
    });
  }

  buildFilterTemplate = (columns = this.state.columns) => {
    const defaultColumn = columns[0] || null;
    const operatorOptions = getOperatorOptionsForType(defaultColumn?.type);

    return {
      id: `filter-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      columnId: defaultColumn?.value ?? null,
      operator: operatorOptions[0]?.value ?? STRING_OPERATORS[0].value,
      value: ""
    };
  };

  loadBoardData = async (boardId) => {
    try {
      const [rawColumns, rawGroups] = await Promise.all([
        getBoardColumns(boardId),
        getBoardGroups(boardId)
      ]);
      const columns = (rawColumns || [])
        .filter((column) => column?.title && ALLOWED_COLUMN_TYPES.has(column?.type))
        .map((column) => ({
          label: column.title,
          value: column.id,
          type: column.type
        }));
      const groups = (rawGroups || []).map((group) => ({
        label: group?.title || "Untitled group",
        value: group?.id
      }));

      this.requireItemIdColumn(columns);

      this.setState((prevState) => {
        const nextFilters = columns.length
          ? (prevState.filters.length
            ? prevState.filters.map((filter) => {
                const columnStillExists = columns.some((column) => column.value === filter.columnId);
                if (columnStillExists) {
                  return filter;
                }
                return {
                  ...filter,
                  columnId: columns[0]?.value ?? null
                };
              })
            // : [this.buildFilterTemplate(columns)])
            : [])
          : [];
        const normalizedFilters = nextFilters.map((filter) => {
          const column = columns.find((column) => column.value === filter.columnId) || null;
          const operatorOptions = getOperatorOptionsForType(column?.type);
          if (operatorOptions.some((option) => option.value === filter.operator)) {
            return filter;
          }

          return {
            ...filter,
            operator: operatorOptions[0]?.value ?? STRING_OPERATORS[0].value
          };
        });
        const validSelectedGroupIds = prevState.selectedGroupIds.filter((groupId) =>
          groups.some((group) => group.value === groupId)
        );

        return {
          columns,
          filters: normalizedFilters,
          groups,
          selectedGroupIds: validSelectedGroupIds,
          isInitializing: false
        };
      });
    } catch (error) {
      console.error(error);
      this.setState({ isInitializing: false });
      showNotice("error", "Failed to load board columns. Please refresh and try again.");
    }
  };

  handleItemsToSelectChange = (value) => {
    const numericValue = Number(value);
    const sanitizedValue = Number.isFinite(numericValue)
      ? Math.min(Math.max(Math.floor(numericValue), 1), MAX_ITEMS_TO_SELECT)
      : DEFAULT_ITEMS_TO_SELECT;

    this.setState({ itemsToSelect: sanitizedValue });
  };

  handleAddFilter = () => {
    const { columns } = this.state;
    if (!columns.length) {
      showNotice("warning", "No columns available to filter.");
      return;
    }

    this.setState((prevState) => ({
      filters: [...prevState.filters, this.buildFilterTemplate(columns)]
    }));
  };

  handleRemoveFilter = (filterId) => {
    this.setState((prevState) => ({
      filters: prevState.filters.filter((filter) => filter.id !== filterId)
    }));
  };

  handleColumnChange = (filterId, selectedOption) => {
    this.updateFilter(filterId, {
      columnId: selectedOption ? selectedOption.value : null
    });
  };

  handleOperatorChange = (filterId, selectedOption) => {
    this.updateFilter(filterId, {
      operator: selectedOption ? selectedOption.value : null
    });
  };

  handleFilterValueChange = (filterId, value) => {
    this.updateFilter(filterId, { value });
  };

  handleGroupChange = (selectedOptions) => {
    const optionsArray = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];

    this.setState({
      selectedGroupIds: optionsArray.map((option) => option.value)
    });
  };

  updateFilter = (filterId, changes) => {
    this.setState((prevState) => ({
      filters: prevState.filters.map((filter) => {
        if (filter.id !== filterId) {
          return filter;
        }

        const nextFilter = { ...filter, ...changes };
        const column = prevState.columns.find((col) => col.value === nextFilter.columnId) || null;
        const operatorOptions = getOperatorOptionsForType(column?.type);
        if (!operatorOptions.some((option) => option.value === nextFilter.operator)) {
          nextFilter.operator = operatorOptions[0]?.value ?? null;
        }

        return nextFilter;
      })
    }));
  };

  handleDrawItems = async () => {
    const { boardId, itemsToSelect, filters, selectedGroupIds } = this.state;

    if (!boardId) {
      showNotice("error", "Board context is missing. Reopen the view from a board.");
      return;
    }

    if (!itemsToSelect || itemsToSelect < 1) {
      showNotice("error", "Set how many items you would like to draw.");
      return;
    }

    const itemIdColumn = this.getItemIdColumn();
    if (!itemIdColumn) {
      this.setState({ showColumnPrompt: true });
      showNotice("error", "Create the Item ID column to draw items.");
      return;
    }

    this.setState({
      isDrawing: true,
      drawnItems: [],
      showResultsModal: true,
      viewNameInput: `Random Picker - ${new Date().toLocaleString()}`,
      createViewLoading: false
    });

    try {
      const selectedItems = await pickRandomItems({
        boardId,
        groupIds: selectedGroupIds,
        filters,
        itemsToSelect
      });

      valueCreated();

      if (!selectedItems.length) {
        showNotice("warning", "No items matched the selected filters.");
      }

      this.setState({
        isDrawing: false,
        drawnItems: selectedItems
      });
    } catch (error) {
      console.error(error);
      this.setState({
        isDrawing: false,
        showResultsModal: false,
        viewNameInput: `Random Picker - ${new Date().toLocaleString()}`
      });
      showNotice("error", error.message || "Unable to draw items. Please try again.");
    }
  };

  renderFilters() {
    const { filters, columns, isInitializing } = this.state;

    if (!filters.length) {
      return (
        <div className={classes.fieldNote}>
          No filters added. Draws will include every item on the board.
        </div>
      );
    }

    return filters.map((filter) => {
      const selectedColumn = columns.find((column) => column.value === filter.columnId) || null;
      const operatorOptions = getOperatorOptionsForType(selectedColumn?.type);
      const selectedOperator = operatorOptions.find((option) => option.value === filter.operator) || operatorOptions[0] || null;

      return (
        <Flex
          key={filter.id}
          align={Flex.align.CENTER}
          gap={Flex.gaps.SMALL}
          direction={Flex.directions.ROW}
          className={classes.filterRow}
        >
          <Dropdown
            className={classes.filterDropdown}
            placeholder={isInitializing ? "Loading columns..." : "Column"}
            options={columns}
            value={selectedColumn}
            disabled={isInitializing || !columns.length}
            onChange={(option) => this.handleColumnChange(filter.id, option)}
          />
          <Dropdown
            className={classes.filterDropdown}
            placeholder="Operator"
            options={operatorOptions}
            value={selectedOperator}
            disabled={isInitializing}
            onChange={(option) => this.handleOperatorChange(filter.id, option)}
          />
          <TextField
            className={classes.filterValue}
            size={TextField.sizes.MEDIUM}
            value={filter.value}
            placeholder="Value"
            onChange={(value) => this.handleFilterValueChange(filter.id, value)}
            disabled={isInitializing}
          />
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            onClick={() => this.handleRemoveFilter(filter.id)}
          >
            Remove
          </Button>
        </Flex>
      );
    });
  }

  getDrawnNames = () => {
    const { drawnItems } = this.state;
    return drawnItems.map((item) => item.name).join(", ");
  };

  getItemIdColumn = (columnsInput) => {
    const columns = columnsInput || this.state.columns;
    return (
      columns.find(
        (column) =>
          column.type === "item_id"
      ) || columns.find((column) => column.type === "item_id") || null
    );
  };

  requireItemIdColumn = (columns) => {
    const hasColumn = !!this.getItemIdColumn(columns);
    this.setState({ showColumnPrompt: !hasColumn });
    return hasColumn;
  };

  handleCreateItemIdColumn = async () => {
    const { boardId } = this.state;
    if (!boardId) {
      return;
    }

    this.setState({ ensureColumnLoading: true });

    try {
      const createdColumn = await createBoardColumn(boardId, ITEM_ID_COLUMN_TITLE, "item_id");
      if (!createdColumn?.id) {
        throw new Error("Failed to create the Item ID column.");
      }

      const newColumn = {
        label: createdColumn.title,
        value: createdColumn.id,
        type: createdColumn.type
      };

      this.setState((prevState) => ({
        columns: [...prevState.columns, newColumn],
        showColumnPrompt: false,
        ensureColumnLoading: false
      }));
      showNotice("success", "Item ID column created successfully.");
    } catch (error) {
      console.error(error);
      showNotice("error", error.message || "Unable to create the Item ID column.");
      this.setState({ ensureColumnLoading: false });
    }
  };

  buildViewFilter = (columnId, itemIds) => ({
    operator: "or",
    rules: [
      {
        column_id: columnId,
        compare_value: itemIds.map((itemId) => String(itemId)),
        compare_attribute: "",
        operator: "any_of"
      }
    ]
  });

  handleCloseModal = () => {
    this.setState((prevState) => ({
      showResultsModal: false,
      viewNameInput: prevState.viewNameInput || `Random Picker - ${new Date().toLocaleString()}`
    }));
  };

  handleCreateView = async () => {
    const { boardId, drawnItems, createViewLoading, viewNameInput } = this.state;
    if (createViewLoading) {
      return;
    }

    if (!boardId) {
      showNotice("error", "Board context is missing. Reopen the view from a board.");
      return;
    }

    if (!drawnItems.length) {
      showNotice("warning", "Draw items before creating a view.");
      return;
    }

    this.setState({ createViewLoading: true });

    try {
      const itemIdColumn = this.getItemIdColumn();
      if (!itemIdColumn) {
        this.setState({
          createViewLoading: false,
          showColumnPrompt: true
        });
        showNotice("error", "Create the Item ID column to continue.");
        return;
      }
      const viewName = viewNameInput || `Random Picker - ${new Date().toISOString()}`;
      const winnerIds = drawnItems.map((item) => item.id);

      if (!winnerIds.length) {
        throw new Error("No winners available to build the view.");
      }

      const filter = this.buildViewFilter(itemIdColumn.value, winnerIds);
      await createBoardView(boardId, viewName, filter);

      showNotice("success", "Filtered view created successfully.");
      this.setState({
        createViewLoading: false,
        showResultsModal: false
      });
    } catch (error) {
      console.error(error);
      showNotice("error", error.message || "Failed to create the view. Please try again.");
      this.setState({ createViewLoading: false });
    }
  };

  renderResultsModal() {
    const { showResultsModal, createViewLoading, drawnItems, viewNameInput, isDrawing } = this.state;

    if (!showResultsModal) {
      return null;
    }

    const names = this.getDrawnNames();
    const viewNamePreview = viewNameInput || `Random Picker - ${new Date().toLocaleString()}`;

    return (
      <Modal
        alertDialog
        id="drawn-items-modal"
        title="Draw results"
        show={showResultsModal}
        width={Modal.width.DEFAULT}
        onClose={this.handleCloseModal}
        contentSpacing
      >
        <ModalContent>
          <div className={classes.resultsSummary}>
            <div className={classes.resultsTitle}>
              {drawnItems.length
                ? `${drawnItems.length} random item${drawnItems.length === 1 ? "" : "s"} selected`
                : "No items were selected."}
            </div>
          {!isDrawing && !!drawnItems.length && (
              <div className={classes.resultsSubtitle}>
              Click "Save to new view" to save them as
              <TextField
                className={classes.viewNameInput}
                size={TextField.sizes.SMALL}
                value={viewNamePreview}
                onChange={(value) => this.setState({ viewNameInput: value })}
                placeholder="Name your view"
                disabled={createViewLoading}
              />
              </div>
            )}
          </div>
          {!isDrawing && !createViewLoading && !!drawnItems.length && (
            <div className={classes.resultsList}>
              {drawnItems.map((item, index) => (
                <div key={item.id} className={classes.resultsListItem}>
                  <span className={classes.resultsListIndex}>{index + 1}.</span>
                  <div className={classes.resultsListDetails}>
                    <div className={classes.resultsListName}>{item.name || "Untitled item"}</div>
                    <div className={classes.resultsListMeta}>Item ID: {item.id}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(isDrawing || createViewLoading) && (
            <div className={classes.loadingOverlay}>
              <div className={classes.loadingSpinner} />
              <div className={classes.loadingText}>
                {isDrawing ? "Drawing items..." : "Creating view... hang tight."}
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText="Save to new view"
          secondaryButtonText="Cancel"
          primaryButtonDisabled={isDrawing || createViewLoading || !drawnItems.length}
          secondaryButtonDisabled={createViewLoading}
          onPrimaryButtonClick={this.handleCreateView}
          onSecondaryButtonClick={this.handleCloseModal}
        />
      </Modal>
    );
  }

  renderColumnPromptModal() {
    const { showColumnPrompt, ensureColumnLoading } = this.state;

    if (!showColumnPrompt) {
      return null;
    }

    return (
      <Modal
        alertDialog
        id="item-id-column-modal"
        title="Item ID column required"
        show={showColumnPrompt}
        width={Modal.width.DEFAULT}
        onClose={() => {}}
        contentSpacing
      >
        <ModalContent>
          <div className={classes.columnPromptText}>
            You need to create a column of type Item ID to track winners. Press "Create Item ID column" to create one now to continue.
          </div>
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText={ensureColumnLoading ? "Creating column..." : "Create Item ID column"}
          hideSecondaryButton
          primaryButtonDisabled={ensureColumnLoading}
          onPrimaryButtonClick={this.handleCreateItemIdColumn}
        />
      </Modal>
    );
  }

  render() {
    const {
      itemsToSelect,
      isInitializing,
      isDrawing,
      columns,
      groups,
      selectedGroupIds,
      showColumnPrompt,
      ensureColumnLoading
    } = this.state;
    const disableDrawButton = isInitializing || isDrawing || showColumnPrompt || ensureColumnLoading;
    const groupDropdownValue = groups.filter((group) => selectedGroupIds.includes(group.value));

    return (
      <Flex align={Flex.align.START} gap={Flex.gaps.MEDIUM} direction={Flex.directions.COLUMN} className={classes.container}>
        <Heading type={Heading.types.H3}>Random Item Picker</Heading>
        <div className={classes.fieldNote}>
          This board view helps you pick a random sample of items. Choose how many items to grab
          and optionally narrow the pool with column filters.
        </div>

        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <div className={classes.formTitle}>How many items do you want to draw?</div>
          <TextField
            className={classes.inputField}
            size={TextField.sizes.MEDIUM}
            value={String(itemsToSelect)}
            type="number"
            min={1}
            max={MAX_ITEMS_TO_SELECT}
            disabled={isInitializing}
            onChange={this.handleItemsToSelectChange}
          />
          <div className={classes.fieldNote}>
            Accepts any value between 1 and {MAX_ITEMS_TO_SELECT}. The draw will be capped based on board size later.
          </div>
        </Flex>

        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <div className={classes.formTitle}>Board group (optional)</div>
          <Dropdown
            className={classes.fullWidth}
            placeholder={isInitializing ? "Loading groups..." : "Select one or more groups"}
            multi
            options={groups}
            value={groupDropdownValue}
            disabled={isInitializing}
            onChange={this.handleGroupChange}
          />
          <div className={classes.fieldNote}>
            Leave empty to consider the entire board or pick multiple groups to narrow the pool.
          </div>
        </Flex>

        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <div className={classes.formTitle}>Filters (optional)</div>
          <div className={classes.fieldNote}>
            Add filters to ensure only matching items are eligible for the draw. Leave this section blank to include everything.
            <br />
            Use YYYY-MM-DD for date values and plain numbers for numeric comparisons.
          </div>
          <div className={classes.filtersWrapper}>
            {this.renderFilters()}
          </div>
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.SECONDARY}
            onClick={this.handleAddFilter}
            disabled={!columns.length || isInitializing}
          >
            Add filter
          </Button>
        </Flex>

        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Button
            size={Button.sizes.MEDIUM}
            kind={Button.kinds.PRIMARY}
            loading={isDrawing}
            disabled={disableDrawButton}
            onClick={this.handleDrawItems}
          >
            Draw items
          </Button>
        </Flex>

        {this.renderResultsModal()}
        {this.renderColumnPromptModal()}
      </Flex>
    );
  }
}

export default RandomItemWizard;
